import type { UserRole } from '@/entities/session/types'
import { canUseAiChat } from '@/shared/lib/roleBasedUtils'
import { SETTINGS_CARDS } from '@/pages/settings/settingsPageConfig'
import type { GoToPageItem } from '@/widgets/navigation/goToPageItems'

export type NavSection = 'primary' | 'more'

export interface AppNavEntry {
  id: string
  to: string
  label: string
  section: NavSection
  keywords?: string[]
  doctorOnly?: boolean
  adminOnly?: boolean
  /** Oculta para perfil paciente (rota /chat-ia). */
  requiresAiChat?: boolean
  state?: Record<string, unknown>
}

export interface NavCatalogContext {
  role: UserRole
  isSystemAdmin: boolean
}

const APP_NAV_ENTRIES: AppNavEntry[] = [
  {
    id: 'dashboard',
    to: '/dashboard',
    label: 'Hoje',
    section: 'primary',
    keywords: ['início', 'home', 'dashboard'],
  },
  {
    id: 'documentos',
    to: '/documentos',
    label: 'Documentos',
    section: 'primary',
    keywords: ['receita', 'atestado', 'prescrição', 'laudo'],
  },
  {
    id: 'patients',
    to: '/patients',
    label: 'Pacientes',
    section: 'primary',
    keywords: ['prontuário', 'ficha'],
  },
  {
    id: 'plantoes',
    to: '/plantoes',
    label: 'Plantões',
    section: 'primary',
    doctorOnly: true,
    keywords: ['escala', 'turno'],
    state: { backTo: '/dashboard' },
  },
  {
    id: 'chat-ia',
    to: '/chat-ia',
    label: 'Chat IA',
    section: 'primary',
    requiresAiChat: true,
    keywords: ['ia', 'assistente', 'synap'],
  },
  {
    id: 'noticias',
    to: '/noticias',
    label: 'Notícias',
    section: 'primary',
    keywords: ['feed', 'rss'],
  },
  {
    id: 'calendar',
    to: '/calendar',
    label: 'Agenda',
    section: 'more',
    keywords: ['calendário', 'consultas'],
    state: { backTo: '/dashboard' },
  },
  {
    id: 'last-results',
    to: '/last-results',
    label: 'Exames',
    section: 'more',
    keywords: ['resultados', 'laboratório'],
  },
  {
    id: 'anatomy',
    to: '/anatomy',
    label: 'Atlas',
    section: 'more',
    keywords: ['anatomia', 'corpo'],
  },
  {
    id: 'inventory',
    to: '/inventory',
    label: 'Estoque',
    section: 'more',
    doctorOnly: true,
    keywords: ['insumos', 'estoque'],
    state: { backTo: '/dashboard' },
  },
  {
    id: 'calculator',
    to: '/calculator',
    label: 'Calculadora',
    section: 'more',
    keywords: ['doses', 'medicamento', 'dcb'],
  },
]

const PALETTE_GROUP_BY_SECTION: Record<NavSection, string> = {
  primary: 'Principal',
  more: 'Mais',
}

export const filterAppNavEntry = (entry: AppNavEntry, ctx: NavCatalogContext): boolean => {
  if (entry.doctorOnly && ctx.role !== 'doctor') return false
  if (entry.adminOnly && !ctx.isSystemAdmin) return false
  if (entry.requiresAiChat && !canUseAiChat(ctx.role)) return false
  return true
}

export const getSidebarNavEntries = (section: NavSection, ctx: NavCatalogContext): AppNavEntry[] =>
  APP_NAV_ENTRIES.filter((e) => e.section === section && filterAppNavEntry(e, ctx))

const toGoToPageItem = (entry: AppNavEntry): GoToPageItem => ({
  to: entry.to,
  label: entry.label,
  group: PALETTE_GROUP_BY_SECTION[entry.section],
  doctorOnly: entry.doctorOnly,
  adminOnly: entry.adminOnly,
  keywords: entry.keywords,
  state: entry.state,
})

/** Itens de página para a paleta (sem pacientes). */
export const getGoToPageItemsFromCatalog = (ctx: NavCatalogContext): GoToPageItem[] => {
  const primary = APP_NAV_ENTRIES.filter((e) => filterAppNavEntry(e, ctx)).map(toGoToPageItem)

  const settingsDeep = SETTINGS_CARDS.filter(
    (card) =>
      (!card.adminOnly || ctx.isSystemAdmin) &&
      (!card.doctorOnly || ctx.role === 'doctor') &&
      !['/calendar', '/plantoes', '/inventory', '/settings'].includes(card.to),
  ).map((card) => ({
    to: card.to,
    label: card.title,
    group: 'Configurações',
    doctorOnly: card.doctorOnly,
    adminOnly: card.adminOnly,
    keywords: keywordsForSettingsTitle(card.title),
    state: card.to === '/inventory' ? { backTo: '/settings' } : undefined,
  }))

  return [...primary, ...settingsDeep]
}

const keywordsForSettingsTitle = (title: string): string[] => {
  const map: Record<string, string[]> = {
    Perfil: ['conta', 'dados pessoais'],
    Configurações: ['ajustes', 'preferências'],
    'O que há de novo': ['novidades', 'versão', 'atualizações', 'changelog'],
    'Enviar feedback': ['sugestão', 'ideia', 'problema'],
  }
  return map[title] ?? []
}

/** Rotas da sidebar (primary + more) que devem existir na paleta com o mesmo destino. */
export const getSidebarPaletteParityPaths = (ctx: NavCatalogContext): string[] =>
  APP_NAV_ENTRIES.filter((e) => filterAppNavEntry(e, ctx)).map((e) => e.to)

/** Minúsculas e sem acento: «prontuario» acha «prontuário», «CONFIGURACOES» acha «Configurações». */
export const normalizeForSearch = (value: string): string =>
  value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim()

export const matchGoToPageQuery = (item: GoToPageItem, query: string): boolean => {
  const q = normalizeForSearch(query)
  if (!q) return true
  const haystack = normalizeForSearch([item.label, item.group, ...(item.keywords ?? [])].join(' '))
  return haystack.includes(q)
}

/** Filtra páginas da busca (paleta e folha Mais) com a mesma regra. */
export const filterGoToPageItems = (items: readonly GoToPageItem[], query: string): GoToPageItem[] =>
  normalizeForSearch(query) ? items.filter((item) => matchGoToPageQuery(item, query)) : [...items]
